import { Injectable } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { DirectService } from '@app/modules/direct/services/direct.service';
import { DirectSiteContent } from '@app/shared/interfaces';
import { PusherService } from '@app/shared/services/pusher/pusher.service';
import { PickerFileMetadata, PickerResponse } from 'filestack-js';
import { debounceTime, distinctUntilChanged, filter, map, Subject, switchMap, takeUntil } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class DirectSiteContentService {
  siteContent: DirectSiteContent[] = [];
  images: string[] = [];
  video?: PickerFileMetadata;
  logo?: string = null;
  form = new FormGroup({
    title: new FormControl(''),
    subtitle: new FormControl(''),
    facebook: new FormControl(''),
    twitter: new FormControl(''),
    instagram: new FormControl(''),
    youtube: new FormControl(''),
    aboutUs: new FormControl(''),
    footer: new FormControl(''),
  });
  loading = false;

  triggerSaveContent$ = new Subject<DirectSiteContent[]>();
  sitePreviewUpdate$ = new Subject<void>();
  refreshIframe$ = new Subject<void>();
  destroy$ = new Subject<void>();

  constructor(
    private direct: DirectService,
    private pusherService: PusherService
  ) {}

  init(siteId: string) {
    this.direct.getRawSiteContent(siteId).subscribe((res) => {
      this.subscribeToSite(siteId);

      this.setSiteContent(res);

      this.handleIframeSrc();
      this.handleFormValueChanges();
    });

    this.triggerSaveContent$
      .pipe(
        switchMap((content) => {
          this.loading = true;
          return this.direct.setRawSiteContent(siteId, content).pipe(map(() => content));
        }),
        takeUntil(this.destroy$)
      )
      .subscribe({
        next: (content) => this.setSiteContent(content),
        error: () => (this.loading = false),
      });
  }

  destroy(siteId: string) {
    this.unsubscribeFromSite(siteId);
    this.setSiteContent([]);
    this.loading = false;
    this.destroy$.next();
  }

  handleIframeSrc() {
    this.refreshIframe$.next();
    this.sitePreviewUpdate$.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.loading = false;
      this.refreshIframe$.next();
    });
  }

  handleFormValueChanges() {
    this.form.valueChanges
      .pipe(debounceTime(1000), distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe(() => this.prepareContent());
  }

  setSiteContent(content: DirectSiteContent[]) {
    this.images = (content.find((content) => content.name === 'hero_gallery')?.value as string[]) || [];
    this.video = (content.find((content) => content.name === 'hero_video')?.value as PickerFileMetadata) || null;
    this.logo = (content.find((content) => content.name === 'business_logo_url')?.value as string) || null;

    this.form.patchValue(
      {
        title: (content.find((content) => content.name === 'hero_title')?.value as string) || '',
        subtitle: (content.find((content) => content.name === 'hero_subtitle')?.value as string) || '',
        facebook: (content.find((content) => content.name === 'facebook')?.value as string) || '',
        twitter: (content.find((content) => content.name === 'twitter')?.value as string) || '',
        instagram: (content.find((content) => content.name === 'instagram')?.value as string) || '',
        youtube: (content.find((content) => content.name === 'youtube')?.value as string) || '',
        aboutUs: (content.find((content) => content.name === 'about_us')?.value as string) || '',
        footer: (content.find((content) => content.name === 'footer')?.value as string) || '',
      },
      { emitEvent: false }
    );

    this.siteContent = [...content];
  }

  onGalleryUploadSuccess(event: PickerResponse) {
    const newUrls = event.filesUploaded?.map(
      (file) => `https://cdn.filestackcontent.com/quality=value:30/compress/${file.handle}`
    );

    this.images = this.images.concat(newUrls);
    this.prepareContent();
  }

  removeImage(index: number) {
    this.images.splice(index, 1);
    this.prepareContent();
  }

  onVideoSuccess(event: PickerResponse) {
    if (event.filesUploaded?.length > 0) {
      this.video = event.filesUploaded[0];
      this.prepareContent();
    }
  }

  removeVideo() {
    this.video = null;
    this.prepareContent();
  }

  onLogoUploadSuccess(event: PickerResponse) {
    this.logo = event.filesUploaded[0]?.url;
    this.prepareContent();
  }

  removeLogo() {
    this.logo = null;
    this.prepareContent();
  }

  private subscribeToSite(siteId: string) {
    this.pusherService.subscribeToSite(siteId);

    this.pusherService.directSitePreviewUpdate$
      .pipe(filter((data) => data.site_id === siteId))
      .subscribe(() => this.sitePreviewUpdate$.next());
  }

  private unsubscribeFromSite(siteId: string) {
    this.pusherService.unsubscribeFromSite(siteId);
  }

  private prepareContent() {
    if (!this.siteContent) return;

    this.triggerSaveContent$.next(
      [...this.siteContent]
        .map((content) => {
          switch (content.name) {
            case 'hero_gallery':
              content.value = this.images;
              break;
            case 'hero_video':
              content.value = this.video;
              break;
            case 'business_logo_url':
              content.value = this.logo;
              break;
            case 'hero_title':
              content.value = this.form.get('title')?.value;
              break;
            case 'hero_subtitle':
              content.value = this.form.get('subtitle')?.value;
              break;
            case 'facebook':
              content.value = this.form.get('facebook')?.value;
              break;
            case 'twitter':
              content.value = this.form.get('twitter')?.value;
              break;
            case 'instagram':
              content.value = this.form.get('instagram')?.value;
              break;
            case 'youtube':
              content.value = this.form.get('youtube')?.value;
              break;
            case 'about_us':
              content.value = this.form.get('aboutUs')?.value;
              break;
            case 'footer':
              content.value = this.form.get('footer')?.value;
              break;
          }

          return content;
        })
        .sort((a, b) => a.order - b.order)
    );
  }
}
